import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { authService, RegisterRequest } from '@/services/authService';
import { analytics } from '@/services/analytics';
import { isSocialAuthCancelled } from '@/native/socialAuth';

interface SignupPageProps {
  entrySource?: string;
  onNavigateToLogin?: (source?: string) => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
  onSignupSuccess?: () => void;
  initialError?: string;
}

const SignupPage: React.FC<SignupPageProps> = ({ entrySource, onNavigateToLogin, onNavigateToTerms, onNavigateToPrivacy, onSignupSuccess, initialError }) => {
  const { theme } = useTheme();
  const { login, isLoading: authLoading } = useAuth();
  const isDarkMode = theme === 'dark';
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>(initialError || '');
  const [success, setSuccess] = useState<string>('');
  const [step, setStep] = useState(1);

  useEffect(() => {
    analytics.track('Signup Modal', {
      source: entrySource || 'unknown'
    });
  }, [entrySource]);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    subscribeToUpdates: false
  });

  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const themeClasses = isDarkMode ? 'text-white' : 'bg-white text-gray-900';
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);

  const handleGoogleSignup = async () => {
    setIsLoading(true);
    setError('');
    try {
      // Sign-up: force Google's account chooser so users pick the right account
      // rather than being silently signed into the device's last-used one.
      await login('google', { forceAccountPicker: true });
    } catch (error) {
      if (!isSocialAuthCancelled(error)) {
        console.error('Google signup failed:', error);
        setError(error instanceof Error ? error.message : 'Google signup failed');
      }
      setIsLoading(false);
    }
  };

  const handleAppleSignup = async () => {
    setIsLoading(true);
    setError('');
    try {
      await login('apple');
    } catch (error) {
      if (!isSocialAuthCancelled(error)) {
        console.error('Apple signup failed:', error);
        setError(error instanceof Error ? error.message : 'Apple signup failed');
      }
      setIsLoading(false);
    }
  };

  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('Password must be at least 8 characters long');
    if (!/[A-Z]/.test(password)) errors.push('Password must contain at least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('Password must contain at least one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('Password must contain at least one number');
    return errors;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: inputValue }));

    if (name === 'password') setPasswordErrors(validatePassword(value));

    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    if (step === 1) {
      setStep(2);
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate form
      if (!formData.name.trim()) {
        setError('Please enter your full name');
        setIsLoading(false);
        return;
      }
      if (!formData.email.trim()) {
        setError('Please enter your email address');
        setIsLoading(false);
        return;
      }
      if (!formData.password) {
        console.log('!!!!!!!!!!!!')
        setError('Please enter a password');
        setIsLoading(false);
        return;
      }

      // Validate password requirements
      const passwordValidationErrors = validatePassword(formData.password);
      if (passwordValidationErrors.length > 0) {
        setError(passwordValidationErrors[0]);
        setIsLoading(false);
        return;
      }

      // Register user
      const registerData: RegisterRequest = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        subscribe_to_updates: formData.subscribeToUpdates
      };

      const response = await authService.registerWithEmail(registerData);

      if (response) {
        analytics.track('Signup Form Submitted', {
          name: formData.name,
          email: formData.email,
          signup_method: 'email',
          newsletter: formData.subscribeToUpdates,
        });
      }

      setSuccess(response.email);

      // Clear form
      setFormData({
        name: '',
        email: '',
        password: '',
        subscribeToUpdates: false
      });
      setPasswordErrors([]);

      if (onSignupSuccess) onSignupSuccess();

    } catch (error) {
      console.error('Email signup error:', error);
      setError(error instanceof Error ? error.message : 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`w-full ${themeClasses} transition-colors duration-300 rounded-lg`}>
      <div className={`w-full min-h-123 max-w-md mx-auto flex flex-col`}>

        {!success && (
          <>
            {/* Page Header */}
            <div className={`text-center ${step === 2 ? 'shrink-0 mb-8 mt-8' : 'mb-8 mt-8'}`}>
              <h1 className="text-2xl 2xs:text-3xl font-regular mb-2">Join Aleph Beta for free</h1>
            </div>

            <div className="mb-6 text-center">
              <h2 className="text-md font-regular mb-2">
                Get 30 minutes of conversation with Aleph,
                <br className="hidden xs:block" />
                <span className="xs:hidden"> </span>
                plus unlimited audiobook listening.
              </h2>
            </div>
          </>
        )}

        {/* Signup Form */}
        <div className="w-full grow flex flex-col">
          <div className="grow flex flex-col">

            {step === 2 && !success && (
              <button
                onClick={() => setStep(1)}
                className={`absolute top-4 left-4 p-2 rounded-full cursor-pointer transition-colors ${
                  isDarkMode
                    ? 'text-gray-400 hover:bg-gray-700 hover:text-white'
                    : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900'
                }`}
                aria-label="Go back"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>
            )}

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm">{error}</span>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="mt-2 p-6 text-center">
                <div className="flex flex-col items-center space-y-4">

                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 7.89a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>

                  {/* Success Title */}
                  <div>
                    <h3 className="text-xl font-semibold text-green-800 mb-2">Check Your Email!</h3>
                    <p className="text-green-700 mb-2">
                      We've sent a verification email to:
                    </p>
                    <p className="text-lg font-medium text-green-800 mb-4">{success}</p>
                    <p className="text-green-700 mb-4">
                      Please click the verification link in the email to complete your account setup.
                    </p>
                  </div>

                  {/* Next Steps */}
                  <div className="bg-white border border-green-200 rounded-lg p-4 w-full">
                    <h4 className="font-medium text-green-800 mb-2">Next Steps:</h4>
                    <ol className="text-sm text-green-700 space-y-1 text-left">
                      <li>1. Check your email inbox (and spam folder)</li>
                      <li>2. Click the verification link in the email</li>
                      <li>3. Return here to sign in to your account</li>
                    </ol>
                  </div>

                  {/* Didn't receive email */}
                  <p className="text-sm text-green-600">
                    Didn't receive the email?{' '}
                    <button
                      onClick={() => {
                        setSuccess('');
                        setFormData({ name: '', email: formData.email, password: '', subscribeToUpdates: false });
                      }}
                      className="font-medium underline hover:no-underline"
                    >
                      Try again
                    </button>
                    {' '}or check your spam folder.
                  </p>
                </div>
              </div>
            )}

            {/* OAuth Buttons */}
            {!success && step === 1 && (
              <div className="grow flex flex-col justify-center space-y-4">
                <div className="space-y-4">
                  {/* Google Signup Button */}
                  <button
                    onClick={handleGoogleSignup}
                    disabled={isLoading || authLoading}
                    className={`w-full flex items-center justify-center px-4 py-3 border border-blue-600 rounded-full shadow-sm text-sm font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                        : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                    } ${(isLoading || authLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}`}
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="none">
                      <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {(isLoading || authLoading) ? 'Creating account...' : 'Sign up with Google'}
                  </button>

                  <button
                    onClick={handleAppleSignup}
                    disabled={isLoading || authLoading}
                    className={`w-full flex items-center justify-center px-4 py-3 border border-blue-600 rounded-full shadow-sm text-sm font-medium transition-colors ${
                      isDarkMode
                        ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                        : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                    } ${(isLoading || authLoading) ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}`}
                  >
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.05 12.54c-.03-3.04 2.48-4.52 2.59-4.59-1.43-2.09-3.65-2.38-4.44-2.4-1.87-.2-3.69 1.12-4.64 1.12-.97 0-2.43-1.1-4.01-1.07-2.03.03-3.93 1.21-4.97 3.04-2.15 3.72-.55 9.18 1.51 12.19 1.03 1.47 2.23 3.1 3.81 3.04 1.55-.06 2.13-.98 4-.98 1.85 0 2.4.98 4.02.94 1.66-.03 2.71-1.47 3.7-2.95 1.19-1.69 1.67-3.36 1.69-3.45-.04-.01-3.23-1.23-3.26-4.89ZM13.98 3.57A5.39 5.39 0 0 0 15.21 0a5.49 5.49 0 0 0-3.55 1.69 5.13 5.13 0 0 0-1.26 3.42 4.53 4.53 0 0 0 3.58-1.54Z"/>
                    </svg>
                    {(isLoading || authLoading) ? 'Creating account...' : 'Sign up with Apple'}
                  </button>
                </div>
              </div>
            )}

            {/* Divider */}
            {!success && step === 1 && (
              <div className="relative mb-6 mt-8">
                <div className="absolute inset-0 flex items-center">
                  <div className={`w-full border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`} />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className={`px-2 ${isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                    Or sign up with email
                  </span>
                </div>
              </div>
            )}

            {/* Signup Form */}
            {!success && (
              <form onSubmit={handleEmailSignup} className={`${step === 2 ? 'grow flex flex-col' : 'space-y-4'}`}>
                {step === 2 && (
                  <div className="grow flex flex-col justify-center space-y-4">
                    <div>
                      <label htmlFor="name" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Full Name
                      </label>
                      <input
                        type="text"
                        ref={nameInputRef}
                        id="name"
                        name="name"
                        autoComplete="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 rounded-full border ${
                          isDarkMode
                            ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                            : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Enter your full name"
                      />
                    </div>

                    <div>
                      <label htmlFor="password" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        name="password"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-3 py-2 rounded-full border ${
                          isDarkMode
                            ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                            : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        placeholder="Create a password"
                      />

                      {/* Password Requirements */}
                      {formData.password && passwordErrors.length > 0 && (
                        <div className="mt-2 text-sm text-red-600">
                          <ul className="list-disc list-inside space-y-1">
                            {passwordErrors.map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {formData.password && passwordErrors.length === 0 && (
                        <div className="mt-2 text-sm text-green-600">✓ Password meets all requirements</div>
                      )}
                    </div>

                    <div className="flex items-center">
                      <input
                        id="updates"
                        name="subscribeToUpdates"
                        type="checkbox"
                        checked={formData.subscribeToUpdates}
                        onChange={handleInputChange}
                        className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500`}
                      />
                      <label htmlFor="updates" className={`ml-2 block text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Send me email updates about Aleph
                      </label>
                    </div>

                    <div className="text-center text-xs text-gray-500 pt-4 mb-4">
                      By continuing, you agree to our{' '}
                      <button
                        type="button"
                        onClick={() => onNavigateToTerms?.()}
                        className={`font-medium hover:underline ${
                          isDarkMode
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-500'
                        }`}
                      >
                        Terms of Service
                      </button>
                      {' '}and our{' '}
                      <button
                        type="button"
                        onClick={() => onNavigateToPrivacy?.()}
                        className={`font-medium hover:underline ${
                          isDarkMode
                            ? 'text-blue-400 hover:text-blue-300'
                            : 'text-blue-600 hover:text-blue-500'
                        }`}
                      >
                        Privacy Notice
                      </button>
                      .
                    </div>

                  </div>
                )}

                {/* Step 1 - Email */}
                {step === 1 && (
                  <div className="grow flex flex-col justify-center">
                    <label htmlFor="email" className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email Address
                    </label>

                    <input
                      type="email"
                      ref={emailInputRef}
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-3 py-2 mb-4 rounded-full border ${
                        isDarkMode
                          ? 'bg-gray-700 border-blue-600 text-white placeholder-gray-400'
                          : 'bg-white border-blue-600 text-gray-900 placeholder-gray-500'
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="Enter your email"
                    />
                  </div>
                )}

                {/* Button*/}
                <div className="shrink-0">
                  <button
                    type="submit"
                    disabled={isLoading || authLoading}
                    className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white transition-colors ${
                      isLoading || authLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : `${
                          isDarkMode
                            ? 'bg-blue-700 border-blue-700 text-white hover:bg-blue-500 hover:border-blue-500'
                            : 'bg-blue-700 border-blue-700 text-white hover:bg-blue-800 hover:border-blue-800'
                        } cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`
                    }`}
                  >
                    {step === 1
                      ? 'Continue'
                      : (isLoading || authLoading)
                        ? 'Creating Account...'
                        : 'Create Account'}
                  </button>
                </div>

              </form>
            )}

            {/* Sign In Link */}
            {!success && (
              <div className={`mt-6 text-center ${step === 2 ? 'invisible' : ''}`}>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Already have an account?{' '}
                  <button
                    onClick={() => onNavigateToLogin?.('signup_modal')}
                    className={`font-medium hover:underline cursor-pointer ${
                      isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'
                    }`}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
